import React from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHeaderInfo } from '../PlayerHandler';

interface PlayerHeaderProps {
  header: PlayerHeaderInfo;
}

const getRankColor = (rank: string) => {
  switch (rank) {
    case 'GrandMaster': return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
    case 'Master': return { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' };
    case 'Expert': return { bg: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
    case 'Professional': return { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15' };
    case 'Apprentice': return { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' };
    case 'Beginner': return { bg: 'rgba(217, 119, 6, 0.2)', color: '#d97706' };
    default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
  }
};

const formatTime = (seconds: number | null | undefined): string => {
  if (seconds == null || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PlayerHeader: React.FC<PlayerHeaderProps> = ({ header }) => {
  const { theme, isMobile } = useTheme();
  const rankColors = getRankColor(header.rankName || '');

  const summaryStats = [
    { label: 'Highest Rating', value: header.highestRating ?? 0 },
    { label: 'Friends', value: header.friendsCount ?? 0 },
    { label: 'Achievements', value: `${header.achievementsCompleted ?? 0}/${header.totalAchievements ?? '?'}` },
    { label: 'VS High Score', value: header.vsHighScore ?? 0 },
    { label: 'Best Time', value: formatTime(header.singleModeBestTime) },
    { label: 'Last Activity', value: formatDate(header.lastActivity) },
  ];

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '10px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    topRow: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '16px',
    },
    avatar: {
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      background: theme.tableHeader,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '22px',
      fontWeight: 700,
      color: theme.textSecondary,
      flexShrink: 0,
    },
    info: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap' as const,
    },
    usernameBlock: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    username: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
    },
    rankBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '3px 10px',
      borderRadius: '14px',
      background: rankColors.bg,
      color: rankColors.color,
      fontWeight: 600,
      fontSize: '12px',
    },
    rankScore: {
      fontSize: '11px',
      opacity: 0.8,
    },
    email: {
      fontSize: '13px',
      color: theme.textSecondary,
    },
    joinDate: {
      fontSize: '12px',
      color: theme.textSecondary,
    },
    onlineDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: header.isOnline ? '#22c55e' : '#6b7280',
      display: 'inline-block',
      marginLeft: '6px',
    },
    statsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginLeft: 'auto',
      flexWrap: 'wrap' as const,
    },
    statBox: {
      textAlign: 'center' as const,
      padding: '4px 8px',
    },
    statLabel: {
      fontSize: '11px',
      color: theme.textSecondary,
      marginBottom: '2px',
      whiteSpace: 'nowrap' as const,
    },
    statValue: {
      fontSize: '16px',
      fontWeight: 700,
      color: theme.text,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.topRow}>
        <div style={styles.avatar}>
          {(header.username || '?')[0].toUpperCase()}
        </div>
        <div style={styles.info}>
          <div style={styles.usernameBlock}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={styles.username}>{header.username}</span>
              <span style={styles.onlineDot} />
            </div>
            <div style={styles.rankBadge}>
              {header.rankName}
              <span style={styles.rankScore}>{header.rankScore}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '2px' }}>
            <span style={styles.email}>{header.email}</span>
            <span style={styles.joinDate}>{formatDate(header.joinDate)}</span>
          </div>
        </div>
        <div style={styles.statsRow}>
          {summaryStats.map(stat => (
            <div key={stat.label} style={styles.statBox}>
              <div style={styles.statLabel}>{stat.label}</div>
              <div style={styles.statValue}>{stat.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlayerHeader;
